import { Button, Card, Space, Typography, message, Modal, Spin } from 'antd';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SlackApiService } from '@src/api';
import SlackSettingsModal from './SlackSettingsModal';
import { useSearchParams } from 'react-router-dom';
import IcoSlack from '@src/assets/svg/IcoSlack';

const { Title, Text } = Typography;

const TabExternalServices = () => {
  const queryClient = useQueryClient();
  const [isSettingsModalVisible, setIsSettingsModalVisible] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // 슬랙 연결 상태 조회
  const { data: connectionStatus, isLoading } = useQuery(
    ['slackConnectionStatus'],
    () => SlackApiService.getConnectionStatus(),
    {
      suspense: false,
      onError: (error) => {
        console.error('슬랙 연결 상태 조회 실패:', error);
      },
    },
  );

  const isSlackConnected = connectionStatus?.data?.connected || false;

  // URL status 파라미터 처리
  useEffect(() => {
    const status = searchParams.get('status');

    if (status === 'SLACK_INTEGRATION_SUCCESS') {
      // 슬랙 연동 성공 - 성공 메시지 표시
      Modal.success({
        title: '연동 완료',
        content:
          '슬랙 워크스페이스 추가가 완료되었습니다. 확인 버튼을 눌러 알림을 전송할 채널을 선택해 주세요.',
        okText: '확인',
        onOk: () => {
          // 설정 모달 열기
          setIsSettingsModalVisible(true);
        },
      });

      // 연결 상태 리프레시
      queryClient.invalidateQueries(['slackConnectionStatus']);

      // URL에서 status 파라미터 제거
      searchParams.delete('status');
      setSearchParams(searchParams);
    } else if (status === 'SLACK_INTEGRATION_FAIL') {
      // 슬랙 연동 실패 - 에러 모달 표시
      Modal.error({
        title: '연동 실패',
        content: '슬랙 워크스페이스 추가 중 에러가 발생했습니다.',
        okText: '확인',
        okButtonProps: {
          style: {
            backgroundColor: '#4403B5',
            borderColor: '#4403B5',
          },
        },
      });

      // URL에서 status 파라미터 제거
      searchParams.delete('status');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, queryClient]);

  // 슬랙 OAuth 시작
  const startOAuthMutation = useMutation(() => SlackApiService.startOAuth(), {
    onSuccess: (response) => {
      if (response?.data?.authUrl) {
        window.location.href = response.data.authUrl;
      }
    },
    onError: () => {
      message.error('슬랙 연결을 시작할 수 없습니다.');
    },
  });

  // 슬랙 연결 해제
  const disconnectMutation = useMutation(
    () => SlackApiService.disconnectSlack(),
    {
      onSuccess: () => {
        message.success('슬랙 연결이 해제되었습니다.');
        queryClient.invalidateQueries(['slackConnectionStatus']);
      },
      onError: () => {
        message.error('슬랙 연결 해제에 실패했습니다.');
      },
    },
  );

  const handleSlackConnect = () => {
    startOAuthMutation.mutate();
  };

  const handleSlackDisconnect = () => {
    Modal.confirm({
      title: '슬랙 연결 해제',
      content: '슬랙 연결을 해제하시겠습니까? 모든 알림 설정이 삭제됩니다.',
      okText: '해제',
      cancelText: '취소',
      okButtonProps: {
        danger: true,
      },
      onOk: () => {
        disconnectMutation.mutate();
      },
    });
  };

  const handleSlackSettings = () => {
    setIsSettingsModalVisible(true);
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 0' }}>
      <Card
        style={{
          marginBottom: '20px',
          borderRadius: '8px',
          border: '1px solid #f0f0f0',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              flex: 1,
            }}
          >
            {/* 슬랙 로고 */}
            <div
              style={{
                width: '48px',
                height: '48px',
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #f0f0f0',
              }}
            >
              <IcoSlack width={28} height={28} />
            </div>

            <div>
              <Title
                level={4}
                style={{ margin: 0, fontSize: '18px', lineHeight: '20px' }}
              >
                Slack
              </Title>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                {isSlackConnected
                  ? '연결됨'
                  : '워크스페이스와 연결하여 알림을 받아보세요'}
              </Text>
            </div>
          </div>

          <div style={{ flexShrink: 0, marginLeft: 'auto' }}>
            {isSlackConnected ? (
              <Space wrap={false}>
                <Button
                  size="large"
                  onClick={handleSlackSettings}
                  style={{
                    minWidth: '80px',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                  }}
                >
                  설정
                </Button>
                <Button
                  size="large"
                  danger
                  onClick={handleSlackDisconnect}
                  style={{
                    minWidth: '80px',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                  }}
                >
                  연결 해제
                </Button>
              </Space>
            ) : (
              <Button
                size="large"
                onClick={handleSlackConnect}
                style={{
                  minWidth: '80px',
                  borderRadius: '6px',
                  fontWeight: 'bold',
                }}
              >
                연결
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* 추후 다른 서비스 추가 시 여기에 카드 컴포넌트 추가 */}

      {/* 슬랙 설정 모달 */}
      <SlackSettingsModal
        visible={isSettingsModalVisible}
        onClose={() => setIsSettingsModalVisible(false)}
      />
    </div>
  );
};

export default TabExternalServices;
